import { Logger } from '../utils/logger';
export function toLocalDatetimeString(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${year}-${month}-${day}T${hours}:${minutes}`;
}

// V4-SCHEDULE-TZ — conversion « heure murale dans un fuseau » → instant UTC absolu.
// Aucune lib de date n'est installée : on inverse l'offset du fuseau à cette date via
// Intl.DateTimeFormat (gère le DST été/hiver). Utilisé par DateTimePicker pour que le
// sélecteur de fuseau soit réellement fonctionnel (l'heure choisie est interprétée dans
// le fuseau sélectionné, pas dans le fuseau du navigateur).

/**
 * Offset (ms) du fuseau `timeZone` à l'instant `date` : (heure murale du fuseau exprimée
 * en UTC) − (UTC réel). Positif à l'est de Greenwich (ex. Europe/Paris été = +7200000).
 */
export function getTimeZoneOffsetMs(date: Date, timeZone: string): number {
  const dtf = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hour12: false,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
  const map: Record<string, number> = {};
  for (const p of dtf.formatToParts(date)) {
    if (p.type !== 'literal') map[p.type] = parseInt(p.value, 10);
  }
  // Intl peut rendre l'heure "24" pour minuit selon l'implémentation → normalise.
  const asUTC = Date.UTC(map.year, map.month - 1, map.day, map.hour % 24, map.minute, map.second);
  return asUTC - date.getTime();
}

/**
 * Interprète une heure murale (year, month 0-based, day, hours, minutes) dans le fuseau
 * `timeZone` et renvoie l'instant UTC absolu correspondant. Robuste au DST (double passe
 * pour corriger l'offset autour des transitions).
 */
export function zonedWallTimeToUtc(
  year: number,
  month: number,
  day: number,
  hours: number,
  minutes: number,
  timeZone: string,
): Date {
  // On pose l'heure murale comme si elle était en UTC, puis on retranche l'offset réel
  // du fuseau à cet instant.
  const naiveUTC = Date.UTC(year, month, day, hours, minutes);
  const offset1 = getTimeZoneOffsetMs(new Date(naiveUTC), timeZone);
  let utc = naiveUTC - offset1;
  // Raffinement DST : si l'offset diffère à l'instant estimé (transition), on recalcule.
  const offset2 = getTimeZoneOffsetMs(new Date(utc), timeZone);
  if (offset2 !== offset1) {
    utc = naiveUTC - offset2;
  }
  return new Date(utc);
}

export function addDays(date: Date, days: number): Date {
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
}

export function getTomorrow(): Date {
  return addDays(new Date(), 1);
}

export function formatDateForDisplay(date: Date | string): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  return d.toLocaleDateString('fr-FR');
}

export function formatDateLong(date: Date | string): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  return d.toLocaleDateString('fr-FR', {
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  });
}

export function formatDateTime(date: Date | string): string {
  try {
    const d = typeof date === 'string' ? new Date(date) : date;
    if (isNaN(d.getTime())) return '';
    return d.toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  } catch (e) {
    Logger.error('Error formatting date time:', e);
    return '';
  }
}

// V4-CONSTANTS-AUDIT-FRONTEND 2026-05-28
// Format court : "5 mars 2026"
export function formatDateShort(date: Date | string): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  if (isNaN(d.getTime())) return '';
  return d.toLocaleDateString('fr-FR', {
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  });
}

// V4-CONSTANTS-AUDIT-FRONTEND 2026-05-28
// Format compact avec heure : "05 mars, 14:32" (sans année, pour affichages chronologiques récents)
export function formatDayMonthTime(date: Date | string): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  if (isNaN(d.getTime())) return '';
  return d.toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit'
  });
}

export function formatMonthYear(date: Date | string, locale: string = 'fr-FR'): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  return d.toLocaleDateString(locale, { month: 'long', year: 'numeric' });
}

export function getWeekDays(locale: string = 'fr-FR'): string[] {
  // Start from a known Monday (e.g., Jan 1, 2024)
  const baseDate = new Date(2024, 0, 1); 
  const weekDays: string[] = [];
  for (let i = 0; i < 7; i++) {
    // Use 'narrow' for single letter (L, M, M...) or 'short' (Lun, Mar...)
    // Here we want single letter to match existing UI
    let day = baseDate.toLocaleDateString(locale, { weekday: 'narrow' });
    // Capitalize just in case
    day = day.charAt(0).toUpperCase();
    weekDays.push(day);
    baseDate.setDate(baseDate.getDate() + 1);
  }
  return weekDays;
}

export function isSameDay(date1: Date, date2: Date): boolean {
  return (
    date1.getDate() === date2.getDate() &&
    date1.getMonth() === date2.getMonth() &&
    date1.getFullYear() === date2.getFullYear()
  );
}

export function isToday(date: Date): boolean {
  return isSameDay(date, new Date());
}

export function isFuture(date: Date): boolean {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const compareDate = new Date(date);
  compareDate.setHours(0, 0, 0, 0);
  return compareDate > today;
}

export function isPast(date: Date): boolean {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const compareDate = new Date(date);
  compareDate.setHours(0, 0, 0, 0);
  return compareDate < today;
}

export function isInRange(date: Date, startDate: Date | null, endDate: Date = new Date()): boolean {
  if (!startDate) return false;
  
  const end = new Date(endDate);
  end.setHours(0, 0, 0, 0);
  
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  
  const start = new Date(startDate);
  start.setHours(0, 0, 0, 0);
  
  return d > start && d < end;
}

export function getCalendarDays(currentMonth: Date): Array<{ date: Date; isCurrentMonth: boolean }> {
  const year = currentMonth.getFullYear();
  const month = currentMonth.getMonth();
  
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  
  const firstDayOfWeek = firstDay.getDay();
  const adjustedFirstDay = firstDayOfWeek === 0 ? 6 : firstDayOfWeek - 1;
  
  const days: Array<{ date: Date; isCurrentMonth: boolean }> = [];
  
  // Previous month overflow
  for (let i = adjustedFirstDay - 1; i >= 0; i--) {
    const date = new Date(year, month, -i);
    days.push({ date, isCurrentMonth: false });
  }
  
  // Current month
  for (let i = 1; i <= lastDay.getDate(); i++) {
    const date = new Date(year, month, i);
    days.push({ date, isCurrentMonth: true });
  }
  
  // Next month overflow
  const remainingCells = 7 - (days.length % 7);
  if (remainingCells < 7) {
    for (let i = 1; i <= remainingCells; i++) {
      const date = new Date(year, month + 1, i);
      days.push({ date, isCurrentMonth: false });
    }
  }
  
  return days;
}

export const getMonthDays = (year: number, month: number) => {
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  
  const firstDayOfWeek = firstDay.getDay();
  const adjustedFirstDay = firstDayOfWeek === 0 ? 6 : firstDayOfWeek - 1;
  
  const prevMonthDays: Date[] = [];
  for (let i = adjustedFirstDay - 1; i >= 0; i--) {
    const date = new Date(year, month, -i);
    prevMonthDays.push(date);
  }
  
  const currentMonthDays: Date[] = [];
  for (let i = 1; i <= lastDay.getDate(); i++) {
    currentMonthDays.push(new Date(year, month, i));
  }
  
  const totalDays = prevMonthDays.length + currentMonthDays.length;
  const remainingDays = totalDays % 7 === 0 ? 0 : 7 - (totalDays % 7);
  const nextMonthDays: Date[] = [];
  for (let i = 1; i <= remainingDays; i++) {
    nextMonthDays.push(new Date(year, month + 1, i));
  }
  
  return [...prevMonthDays, ...currentMonthDays, ...nextMonthDays];
};
